import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaSearch, FaClone } from 'react-icons/fa';
import Btn from '../../components/ui/Btn';
import StatusTag from '../../components/ui/StatusTag';
import { NativeSelect, TextInput } from '../../components/ui/Field';
import ReadinessTrack from './components/ReadinessTrack';
import { EnvironmentTag } from './components/EnvironmentPicker';
import { ENVIRONMENTS, envOf } from '../../utils/agentEnvironment';
import AgentDot from '../../components/ui/AgentDot';
import { agentLookOf } from '../../utils/agentLook';
import { readiness, hasUnpublishedChanges, CHANNEL_NAMES, isExternal, MANAGED_LABELS, EXTERNAL_MEANING } from '../../utils/agentReadiness';

const FILTERS = [
  { key: 'active', label: 'En uso' },
  { key: 'draft', label: 'Sin publicar' },
  { key: 'published', label: 'Publicados' },
  { key: 'external', label: 'Externos' },
  { key: 'archived', label: 'Archivados' },
];

export function statusTag(agent, draft, published) {
  if (agent.status === 'archived') return <StatusTag>Archivado</StatusTag>;
  if (isExternal(agent)) return <StatusTag>Externo · {MANAGED_LABELS[agent.managed_by]}</StatusTag>;
  if (agent.status !== 'published') return <StatusTag>Borrador</StatusTag>;
  if (hasUnpublishedChanges(draft, published)) return <StatusTag tone="caution">Cambios sin publicar</StatusTag>;
  return <StatusTag tone="live" dot>Publicado</StatusTag>;
}

const money = (n) => (n == null ? '—' : n > 0 && n < 0.01 ? 'menos de $0.01' : `$${n.toFixed(2)}`);

// Presentación pura: recibe los agentes ya cargados.
export default function AgentsList({ agents, projects, loading, error, onNew, onClone, onRetry }) {
  const [q, setQ] = useState('');
  const [project, setProject] = useState('');
  const [filter, setFilter] = useState('active');
  const [env, setEnv] = useState('');

  const rows = useMemo(() => agents
    .filter((a) => (filter === 'active' ? a.status !== 'archived'
      : filter === 'external' ? isExternal(a) && a.status !== 'archived'
      : filter === 'draft' ? a.status === 'draft' && !isExternal(a)
      : a.status === filter))
    .filter((a) => !project || String(a.project_id) === project)
    .filter((a) => !env || envOf(a) === env)
    .filter((a) => !q.trim() || `${a.name} ${a.project?.name || ''}`.toLowerCase().includes(q.trim().toLowerCase()))
    .map((a) => ({ a, r: readiness({ agent: a, project: a.project, credential: a.credential, draft: a.draft, published: a.published, connections: a.connections }) })),
  [agents, filter, project, env, q]);

  const projectsWithAgents = useMemo(() => {
    const ids = new Set(agents.map((a) => a.project_id));
    return projects.filter((p) => ids.has(p.id)).sort((x, y) => (x.name || '').localeCompare(y.name || '', 'es'));
  }, [agents, projects]);

  const platform = rows.filter(({ a }) => !isExternal(a));
  const external = rows.filter(({ a }) => isExternal(a));

  return (
    <div className="max-w-6xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-[34px] sm:text-[40px] leading-[1.05] font-bold text-fg">Agentes</h1>
          <p className="mt-2 text-[15px] text-fg-dim max-w-[58ch]">Crea, entrena y conecta los agentes de cada cliente. Los proyectos que aún atiende n8n siguen igual hasta que enciendas el motor.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onClone && <Btn variant="ghost" size="lg" icon={FaClone} onClick={onClone}>Clonar agente</Btn>}
          <Btn variant="primary" size="lg" icon={FaPlus} onClick={onNew}>Nuevo agente</Btn>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-6">
        <div className="relative flex-1 min-w-0">
          <FaSearch aria-hidden className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-faint" />
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar agente o proyecto" aria-label="Buscar agentes" className="pl-10 rounded-full" />
        </div>
        <NativeSelect value={project} onChange={(e) => setProject(e.target.value)} aria-label="Filtrar por proyecto" className="lg:w-64 rounded-full">
          <option value="">Todos los proyectos</option>
          {projectsWithAgents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </NativeSelect>
        {agents.some((a) => envOf(a)) && (
          <NativeSelect value={env} onChange={(e) => setEnv(e.target.value)} aria-label="Filtrar por ambiente" className="lg:w-48 rounded-full">
            <option value="">Todos los ambientes</option>
            {ENVIRONMENTS.map((e) => <option key={e.key} value={e.key}>{e.label} ({agents.filter((a) => envOf(a) === e.key && a.status !== 'archived').length})</option>)}
          </NativeSelect>
        )}
        <div role="radiogroup" aria-label="Estado" className="flex rounded-full bg-raised p-1 ring-1 ring-inset ring-line overflow-x-auto [scrollbar-width:none]">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="radio" aria-checked={filter === f.key} onClick={() => setFilter(f.key)}
              className={`h-8 px-3.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60
                ${filter === f.key ? 'bg-panel text-fg shadow-sm ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{f.label}</button>
          ))}
        </div>
      </div>

      {error ? (
        <div role="alert" className="rounded-3xl border border-signal/30 bg-signal-soft p-6 text-sm text-fg">
          <p className="font-semibold">No se pudieron cargar los agentes.</p>
          <p className="mt-1 text-fg-dim">{error}</p>
          <Btn size="sm" className="mt-3" onClick={onRetry}>Reintentar</Btn>
        </div>
      ) : loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" aria-label="Cargando agentes">
          {[0, 1, 2].map((i) => <div key={i} className="h-[220px] rounded-3xl skeleton opacity-50" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-fg/15 px-6 py-16 flex flex-col items-center text-center">
          <AgentDot name="Nuevo" palette="aurora" size={72} state={agents.length ? 'idle' : 'happy'} />
          <p className="mt-5 font-display text-[20px] font-bold text-fg">{agents.length ? 'Ningún agente coincide con el filtro' : 'Todavía no hay agentes'}</p>
          <p className="mt-1 text-sm text-fg-dim">{agents.length ? 'Cambia la búsqueda o el estado.' : 'Crea el primero: el proyecto del cliente se puede crear en el mismo paso.'}</p>
          {!agents.length && <Btn variant="primary" icon={FaPlus} className="mt-6" onClick={onNew}>Nuevo agente</Btn>}
        </div>
      ) : (<>
        {platform.length > 0 && (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {platform.map(({ a, r }) => <li key={a.id}><AgentCard a={a} r={r} /></li>)}
          </ul>
        )}
        {external.length > 0 && (
          <section className={platform.length ? 'mt-12' : ''} aria-labelledby="externos-titulo">
            <h2 id="externos-titulo" className="font-display text-[20px] font-bold text-fg">Externos <span className="text-fg-faint font-semibold">{external.length}</span></h2>
            <p className="mt-1 mb-4 text-[13px] leading-relaxed text-fg-faint max-w-[90ch]">{EXTERNAL_MEANING}</p>
            <ul className="rounded-3xl bg-panel ring-1 ring-line divide-y divide-line overflow-hidden">
              {external.map(({ a }) => (
                <li key={a.id}>
                  <Link to={`/agentes/${a.id}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-raised/50 focus-visible:outline-none focus-visible:bg-raised/70 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-live/50">
                    <AgentDot name={a.name} size={30} state="sleep" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-fg truncate">{a.name}</p>
                      <p className="text-[12.5px] text-fg-faint truncate">{a.project?.name || 'Proyecto'} (ID {a.project_id}) · se administra en {MANAGED_LABELS[a.managed_by]}{a.external_note ? ` · ${a.external_note}` : ''}</p>
                    </div>
                    {a.status === 'archived' && <StatusTag>Archivado</StatusTag>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </>)}
    </div>
  );
}

/** Tarjeta de un agente de la plataforma: su avatar con su color, estado, puesta en marcha, canales y gasto. */
function AgentCard({ a, r }) {
  const [hover, setHover] = useState(false);
  const look = agentLookOf(a);
  const pal = look.colors;
  const live = a.connections.filter((c) => ['connected', 'degraded'].includes(c.status));
  const answering = a.status === 'published' && a.project?.engine === 'runtime';
  const state = a.status === 'archived' ? 'sleep' : hover ? 'happy' : 'idle';
  return (
    <Link to={`/agentes/${a.id}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onFocus={() => setHover(true)} onBlur={() => setHover(false)}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl bg-panel ring-1 ring-line p-5 transition duration-200
        hover:-translate-y-0.5 hover:ring-fg/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70"
      style={{ boxShadow: 'var(--shadow)' }}>
      <span aria-hidden className="pointer-events-none absolute -top-16 -left-10 h-40 w-56 rounded-full blur-3xl opacity-25 transition-opacity duration-300 group-hover:opacity-40"
        style={{ background: `radial-gradient(closest-side, ${pal.b}, ${pal.a}, transparent)` }} />
      <div className="relative flex items-start gap-4">
        <AgentDot name={a.name} colors={look.colors} shape={look.avatar} imageUrl={look.avatarUrl} follow={look.follow} size={56} state={state} />
        <div className="min-w-0 flex-1 pt-1">
          <p className="font-display text-[19px] leading-tight font-bold text-fg truncate">{a.name}</p>
          <p className="mt-0.5 text-[13px] text-fg-dim truncate">{a.project?.name || 'Proyecto'} (ID {a.project_id})</p>
        </div>
      </div>
      <div className="relative mt-4 flex flex-wrap items-center gap-2">
        {statusTag(a, a.draft, a.published)}
        <EnvironmentTag agent={a} />
        {a.visibility === 'public' && <StatusTag>Plantilla pública</StatusTag>}
        {answering ? <StatusTag tone="live" dot>Respondiendo</StatusTag> : a.status !== 'archived' && <StatusTag>{a.project?.engine === 'runtime' ? 'Motor encendido' : 'Motor apagado'}</StatusTag>}
      </div>
      <div className="relative mt-5"><ReadinessTrack steps={r.steps} compact /></div>
      <div className="relative mt-auto pt-5 flex items-end justify-between gap-3 text-[13px]">
        <span className="text-fg-dim truncate">{live.length ? live.map((c) => CHANNEL_NAMES[c.channel] || c.channel).join(' · ') : <span className="text-fg-faint">Sin canales</span>}</span>
        <span className="tabular-nums text-fg-dim text-right shrink-0">
          {money(a.monthCost?.usd)}<span className="text-fg-faint"> este mes</span>
          {a.monthCost?.withoutCost > 0 && <span className="block text-[11.5px] text-caution">{a.monthCost.withoutCost} sin precio</span>}
        </span>
      </div>
    </Link>
  );
}
